import { useEffect, useState } from "react";
import { parseISO, format, differenceInCalendarDays } from "date-fns";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { sortFormResponses, sortSectionQuestions } from "@/utils/sort-form-responses";

// Define custom styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 12,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    flexDirection: "column",
  },
  content: {
    padding: "0px 40px",
    flexGrow: 1,
  },
  formHeader: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 20,
    marginTop: 10,
    color: "#1a1a1a",
    textAlign: "center",
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 12,
    color: "#1a1a1a",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 4,
  },
  question: {
    fontSize: 10,
    fontWeight: 600,
    marginBottom: 4,
    color: "#374151",
  },
  responseContainer: {
    marginBottom: 6,
  },
  responseText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#4b5563",
    fontWeight: 400,
  },
  errorText: {
    fontSize: 12,
    marginBottom: 12,
    color: "#ef4444",
  },
  fileLink: {
    color: "#2563eb",
    textDecoration: "underline",
  },
  header: {
    width: "100%",
  },
  footer: {
    width: "100%",
  },
  pageNumber: {
    textAlign: "center",
    fontSize: 10,
    color: "#6b7280",
    marginVertical: 4,
  },
  headerImage: {
    width: "100%",
    // height: 80,
    objectFit: "cover",
    marginBottom: 20,
  },
  footerImage: {
    width: "100%",
    // height: 80,
    objectFit: "cover",
    marginTop: 20,
  },
  checkboxItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  checkboxSymbol: {
    marginRight: 8,
    fontSize: 12,
  },
  dateRangeText: {
    color: "#4b5563",
    fontSize: 12,
    marginTop: 2,
  },
  uploadedSignature: {
    maxWidth: 80,
    height: "auto",
    marginTop: 4,
  },
  uploadedImage: {
    maxWidth: 200,
    height: "auto",
    marginTop: 4,
  },
  calculationItem: {
    flexDirection: "row",
    marginBottom: 6,
    alignItems: "center",
  },
  calculationLabel: {
    width: "33%",
    fontSize: 10,
    fontWeight: 600,
    color: "#374151",
  },
  calculationValue: {
    flex: 1,
    fontSize: 10,
    color: "#4b5563",
    lineHeight: 1.6,
  },
  mapPreview: {
    width: "100%",
    height: 220,
    objectFit: "cover",
    marginTop: 8,
  },
});

type FlightPlanGeometry = GeoJSON.Geometry;

const collectCoordinates = (geometry: FlightPlanGeometry): [number, number][] => {
  const out: [number, number][] = [];

  const visit = (value: any) => {
    if (!Array.isArray(value)) return;

    if (typeof value[0] === "number" && typeof value[1] === "number") {
      out.push([value[0], value[1]]);
      return;
    }

    value.forEach(visit);
  };

  if (geometry.type === "GeometryCollection") {
    geometry.geometries.forEach((g) => {
      visit((g as any).coordinates);
    });
    return out;
  }

  visit((geometry as any).coordinates);
  return out;
};

const simplifyCoordinates = (geometry: FlightPlanGeometry, maxPoints = 220): FlightPlanGeometry => {
  if (geometry.type === "GeometryCollection") {
    return {
      ...geometry,
      geometries: geometry.geometries.map((g) => simplifyCoordinates(g, maxPoints)),
    };
  }

  const all = collectCoordinates(geometry);
  if (all.length <= maxPoints) return geometry;

  const step = Math.max(1, Math.ceil(all.length / maxPoints));

  const simplifyNested = (value: any): any => {
    if (!Array.isArray(value)) return value;

    if (typeof value[0] === "number" && typeof value[1] === "number") {
      return value;
    }

    const isPositionArray =
      Array.isArray(value[0]) &&
      typeof value[0][0] === "number" &&
      typeof value[0][1] === "number";

    if (isPositionArray) {
      const sampled = value.filter((_: any, idx: number) => idx % step === 0);
      const last = value[value.length - 1];

      if (sampled.length === 0) sampled.push(value[0]);
      if (sampled[sampled.length - 1] !== last) sampled.push(last);

      return sampled;
    }

    return value.map(simplifyNested);
  };

  return {
    ...geometry,
    coordinates: simplifyNested(geometry.coordinates),
  };
};

const getGeometryBounds = (geometry: FlightPlanGeometry) => {
  const points = collectCoordinates(geometry);
  if (!points.length) return null;

  let minLon = Number.POSITIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLon = Number.NEGATIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;

  points.forEach(([lon, lat]) => {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    minLon = Math.min(minLon, lon);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lon);
    maxLat = Math.max(maxLat, lat);
  });

  if (![minLon, minLat, maxLon, maxLat].every(Number.isFinite)) return null;
  return { minLon, minLat, maxLon, maxLat };
};

const getFlightPlanStaticMapUrl = (geometry: FlightPlanGeometry) => {
  const token = import.meta.env.VITE_MAPBOX_TOKEN;
  if (!token) return null;

  const simplified = simplifyCoordinates(geometry);
  const featureProps = {
    stroke: "#111827",
    "stroke-width": 2,
    "stroke-opacity": 1,
    fill: "#ef4444",
    "fill-opacity": 0.25,
  };

  const overlayGeoJson =
    simplified.type === "GeometryCollection"
      ? {
          type: "FeatureCollection",
          features: simplified.geometries.map((g) => ({
            type: "Feature",
            properties: featureProps,
            geometry: g,
          })),
        }
      : {
          type: "Feature",
          properties: featureProps,
          geometry: simplified,
        };

  const encoded = encodeURIComponent(JSON.stringify(overlayGeoJson));
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${encoded})/auto/900x420?padding=48&access_token=${token}`;
};

const formatCountryValue = (value: any): string => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          return item.name || item.country || item.code || "";
        }
        return "";
      })
      .filter(Boolean);

    return normalized.length ? normalized.join(", ") : "Not provided";
  }

  if (value && typeof value === "object") {
    return value.name || value.country || value.code || "Not provided";
  }

  return "Not provided";
};

const UserResponsesPDF = ({
  responses,
  allImages,
  showHeaderFooter = true,
}: {
  responses: any;
  allImages: any;
  showHeaderFooter?: boolean;
}) => {
  const [headerImage, setHeaderImage] = useState<string | null>(null);
  const [footerImage, setFooterImage] = useState<string | null>(null);

  useEffect(() => {
    
    if (allImages && showHeaderFooter) {
      
      // allImages is ImageData[] (array of ImageData objects)
      const header = allImages.find(
        (img: { type: string }) => img.type === "HEADER"
      );
      const footer = allImages.find(
        (img: { type: string }) => img.type === "FOOTER"
      );
      
      if (header) {
        setHeaderImage(header.fileUrl);
      }
      if (footer) {
        setFooterImage(footer.fileUrl);
      }
    } else {
      setHeaderImage(null);
      setFooterImage(null);
    }
  }, [allImages, showHeaderFooter]);

  const formatDate = (date: string) => {
    try {
      return format(parseISO(date), "MMMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const getFileExtension = (filename: string) => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  const renderResponse = (type: string, response: any) => {
    if (!type)
      return <Text style={styles.errorText}>Question type missing</Text>;

    switch (type) {
      case "Short Text":
      case "Email":
      case "Phone Number":
      case "Number":
      case "Paragraph":
      case "Dropdown":
      case "Countries":
      case "Country":
        return (
          <Text style={styles.responseText}>
            {type === "Countries" || type === "Country"
              ? formatCountryValue(response)
              : response || "Not provided"}
          </Text>
        );

      case "Date":
        return (
          <Text style={styles.responseText}>{formatDate(response.date)}</Text>
        );

      case "Time":
        return (
          <Text style={styles.responseText}>{response || "Not provided"}</Text>
        );

      case "DateTime":
        if (response?.date && response?.time) {
          return (
            <Text style={styles.responseText}>
              {formatDate(response.date)} at {response.time}
            </Text>
          );
        }
        return <Text style={styles.responseText}>Not provided</Text>;

      case "DateRange":
        if (response?.start && response?.end) {
          const startDate = formatDate(response.start);
          const endDate = formatDate(response.end);
          const totalDays =
            differenceInCalendarDays(
              parseISO(response.end),
              parseISO(response.start)
            ) + 1;

          return (
            <View>
              <Text style={styles.responseText}>
                {startDate} - {endDate}
              </Text>
              <Text style={styles.dateRangeText}>
                Duration: {totalDays > 0 ? totalDays : 0} days
              </Text>
            </View>
          );
        }
        return <Text style={styles.responseText}>Not provided</Text>;

      case "Checkbox":
        return response && Array.isArray(response) ? (
          <View>
            {response.map(
              (
                { option, checked }: { option: string; checked: boolean },
                index: number
              ) => (
                <View key={index} style={styles.checkboxItem}>
                  <Text style={styles.checkboxSymbol}>
                    {checked ? "☑" : "☐"}
                  </Text>
                  <Text style={styles.responseText}>{option}</Text>
                </View>
              )
            )}
          </View>
        ) : (
          <Text style={styles.responseText}>No options selected</Text>
        );

      case "Signature":
        return response ? (
          <Image
            src={`/api/uploads/${response}`}
            style={styles.uploadedSignature}
          />
        ) : (
          <Text style={styles.responseText}>No signature provided</Text>
        );

      case "Upload":
        if (response) {
          const fileExtension = getFileExtension(response);
          const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg"];

          if (imageExtensions.includes(fileExtension)) {
            return (
              <Image
                src={`/api/uploads/${response}`}
                style={styles.uploadedImage}
              />
            );
          }
          return <Text style={styles.fileLink}>{response}</Text>;
        }
        return <Text style={styles.responseText}>No file uploaded</Text>;

      case "Add Calculation":
        if (response?.calculations && Array.isArray(response.calculations)) {
          return (
            <View>
              {response.calculations.map(
                (calc: { name: string; result: number }, index: number) => (
                  <View key={index} style={styles.calculationItem}>
                    <Text style={styles.calculationLabel}>{calc.name}</Text>
                    <Text style={styles.calculationValue}>
                      {isNaN(calc.result) ? "Error" : calc.result.toFixed(2)}
                    </Text>
                  </View>
                )
              )}
            </View>
          );
        }
        return (
          <Text style={styles.responseText}>No calculations provided</Text>
        );

      case "Flight Plan":
      case "MyMap": {
        const geometry = response?.geometry as FlightPlanGeometry | undefined;
        if (!geometry || !geometry.type) {
          return <Text style={styles.responseText}>No MyMap selection provided</Text>;
        }
        const staticMapUrl = getFlightPlanStaticMapUrl(geometry);

        return (
          <View>
            {staticMapUrl ? (
              <Image src={staticMapUrl} style={styles.mapPreview} />
            ) : (
              <Text style={styles.responseText}>
                MyMap selection captured.
              </Text>
            )}
          </View>
        );
      }

      default:
        return <Text style={styles.errorText}>Unsupported question type</Text>;
    }
  };

  if (!responses || !Array.isArray(responses)) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text style={styles.errorText}>Invalid responses format</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {responses.map((responseBlock: any, blockIndex: number) => (
        <Page key={blockIndex} style={styles.page}>
          {/* Header Image on every page */}
           {headerImage && (
             <View style={styles.header} fixed>
               <Image
                 src={headerImage}
                 style={styles.headerImage}
               />
             </View>
           )}

          {/* Main Content */}
          <View style={styles.content}>
            <Text style={styles.formHeader}>
              {responseBlock.formName || responseBlock.name || "Unnamed Form"}
            </Text>

            {/* Handle both old array format and new object format */}
            {(() => {
              const sections = Array.isArray(responseBlock.responses)
                ? responseBlock.responses
                : sortFormResponses(responseBlock.responses || {}, responseBlock.formDesign);
              
              return sections?.map((section: any, sectionIndex: number) => {
                const questions = Array.isArray(section.responses)
                  ? section.responses
                  : sortSectionQuestions(
                      section.questions || section.responses || {},
                      responseBlock.formDesign,
                      section.sectionId || ''
                    );
                
                return (
                  <View
                    key={`${blockIndex}-${sectionIndex}`}
                    style={{ marginBottom: 12 }}
                  >
                    <Text style={styles.sectionHeader}>
                      {section.sectionName || "Unnamed Section"}
                    </Text>

                    {questions && questions.length > 0 ? (
                      questions.map((question: any, questionIndex: number) => (
                        <View
                          key={`${blockIndex}-${sectionIndex}-${questionIndex}`}
                          style={styles.responseContainer}
                        >
                          <Text style={styles.question}>
                            {question.label || "Unnamed Question"}
                          </Text>
                          {renderResponse(question.questionType, question.response)}
                        </View>
                      ))
                    ) : (
                      <Text style={styles.responseText}>
                        No questions in this section
                      </Text>
                    )}
                  </View>
                );
              });
            })() || <Text style={styles.responseText}>No responses found</Text>}
          </View>

          {/* Footer and Pagination on every page */}
           {footerImage && (
             <View style={styles.footer} fixed>
               <Image
                 src={footerImage}
                 style={styles.footerImage}
               />
             </View>
           )}
        </Page>
      ))}
    </Document>
  );
};

export default UserResponsesPDF;
